/**
 * UX States — الحالات الثماني المعتمدة.
 *
 * ملاحظة ملزمة من Design Freeze:
 *   Low Confidence ليست Error. Partial Data ليست Error. No Data ليست Error.
 */

import type { ReactNode } from 'react';
import { color } from '@/design/tokens';
import { Button } from '@/components/primitives/Primitives';

// --------------------------------------------------------------- Loading

export function LoadingState({
  message = 'نقرأ صفقات ٩٠ يوماً...',
  blocks = 3,
}: {
  message?: string;
  blocks?: number;
}): JSX.Element {
  return (
    <div className="nq-card" role="status" aria-live="polite" data-testid="state-loading">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          className="nq-pulse"
          aria-hidden="true"
          style={{ width: 8, height: 8, borderRadius: '50%', background: color.teal }}
        />
        <span style={{ fontSize: 13.5, color: color.textMuted }}>{message}</span>
      </div>
      <div style={{ display: 'grid', gap: 10, marginTop: 18 }}>
        {Array.from({ length: blocks }, (_, i) => (
          <div
            key={i}
            className="nq-skeleton"
            style={{ height: i === 0 ? 34 : 16, width: i === 2 ? '62%' : '100%' }}
          />
        ))}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- Empty

export function EmptyState({
  title,
  description,
  actionLabel,
  onAction,
}: {
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
}): JSX.Element {
  return (
    <div
      data-testid="state-empty"
      style={{
        border: `1px dashed ${color.hairlineHard}`,
        borderRadius: 16,
        padding: 34,
        textAlign: 'center',
      }}
    >
      <h3 style={{ fontSize: 16, fontWeight: 600 }}>{title}</h3>
      <p style={{ marginTop: 8, fontSize: 13.5, color: color.textSubtle, lineHeight: 1.7 }}>
        {description}
      </p>
      {actionLabel && onAction ? (
        <div style={{ marginTop: 18, display: 'flex', justifyContent: 'center' }}>
          <Button onClick={onAction}>{actionLabel}</Button>
        </div>
      ) : null}
    </div>
  );
}

// --------------------------------------------------------------- No Data

export function NoDataState({
  onWatch,
  onExplore,
  diagnosis,
}: {
  onWatch?: () => void;
  onExplore?: () => void;
  /** تشخيص من المحرك — يستبدل النص الثابت بالحقائق الفعلية. */
  diagnosis?: {
    headline: string;
    facts: string[];
    suggestion: string | null;
  } | null;
}): JSX.Element {
  return (
    <div
      data-testid="state-no-data"
      style={{
        border: `1px dashed ${color.hairlineHard}`,
        borderRadius: 16,
        padding: 30,
      }}
    >
      <h3 style={{ fontSize: 17, fontWeight: 600 }}>
        {diagnosis?.headline ?? 'لا نستطيع إعطاء نطاق قيمة موثوق'}
      </h3>

      {/* الأرقام التي بُني عليها الرفض — لا نص عام يناقض ما يراه في شاشة أخرى. */}
      {diagnosis && diagnosis.facts.length > 0 ? (
        <div
          data-testid="rejection-facts"
          style={{
            marginTop: 14,
            padding: '12px 14px',
            background: color.panel600,
            borderRadius: 12,
            display: 'grid',
            gap: 7,
          }}
        >
          {diagnosis.facts.map((f) => {
            const [label, value] = f.split(/:\s*/);
            return (
              <div
                key={f}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  fontSize: 12.5,
                }}
              >
                <span style={{ color: color.textSubtle }}>{label}</span>
                {value ? (
                  <span className="nq-num" dir="ltr" style={{ color: color.text }}>
                    {value}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      <p style={{ marginTop: 12, fontSize: 13, color: color.textSubtle, lineHeight: 1.8 }}>
        {diagnosis?.suggestion ??
          'عدد الصفقات المسجّلة في هذا الحي خلال المدة الأخيرة أقل من الحد الذي نعتمده. لن نعرض رقماً لا نثق فيه.'}
      </p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18 }}>
        {onWatch ? <Button onClick={onWatch}>راقب الحي</Button> : null}
        {onExplore ? (
          <Button variant="ghost" onClick={onExplore}>
            استكشف أحياء مجاورة
          </Button>
        ) : null}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- Error

export function ErrorState({
  message = 'لم نستطع الوصول إلى بيانات الحي الآن.',
  onRetry,
  onEdit,
}: {
  message?: string;
  onRetry?: () => void;
  onEdit?: () => void;
}): JSX.Element {
  return (
    <div className="nq-card" data-testid="state-error" role="alert">
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          padding: '5px 11px',
          borderRadius: 20,
          background: 'rgba(255,92,108,.1)',
          border: '1px solid rgba(255,92,108,.32)',
          color: color.red,
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        <span
          aria-hidden="true"
          style={{ width: 6, height: 6, borderRadius: '50%', background: color.red }}
        />
        خطأ
      </span>
      <p style={{ marginTop: 14, fontSize: 17, fontWeight: 500 }}>{message}</p>
      <p style={{ marginTop: 8, fontSize: 13, color: color.textSubtle }}>
        مدخلاتك محفوظة — يمكنك إعادة المحاولة أو تعديلها.
      </p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 18 }}>
        {onRetry ? <Button onClick={onRetry}>إعادة المحاولة</Button> : null}
        {onEdit ? (
          <Button variant="ghost" onClick={onEdit}>
            تعديل المدخلات
          </Button>
        ) : null}
      </div>
    </div>
  );
}

// -------------------------------------------------------- Low Confidence

/** تنبيه الثقة المنخفضة — كهرماني، وليس أحمر، ولا يُعامل كخطأ. */
export function LowConfidenceNotice({ score }: { score: number }): JSX.Element {
  return (
    <div
      data-testid="state-low-confidence"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '12px 14px',
        borderRadius: 12,
        background: 'rgba(240,162,75,.08)',
        border: '1px solid rgba(240,162,75,.28)',
        color: color.amberText,
        fontSize: 13,
        lineHeight: 1.7,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: color.amber,
          marginTop: 7,
          flex: '0 0 auto',
        }}
      />
      <span>
        الثقة في التقييم منخفضة (<span className="nq-num" dir="ltr">{score}%</span>) — تعامل مع نطاق
        القيمة كتقدير أولي، لا كرقم نهائي.
      </span>
    </div>
  );
}

// ---------------------------------------------------------- Partial Data

export function PartialDataBadge({
  used,
  expected,
}: {
  used: number;
  expected: number;
}): JSX.Element {
  return (
    <span
      data-testid="state-partial"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 9px',
        borderRadius: 6,
        border: '1px solid rgba(240,162,75,.32)',
        color: color.amber,
        fontSize: 11.5,
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      بيانات جزئية — <span className="nq-num" dir="ltr">{used}</span> من{' '}
      <span className="nq-num" dir="ltr">{expected}</span> صفقات
    </span>
  );
}

// --------------------------------------------------------------- Offline

export function OfflineBanner(): JSX.Element {
  return (
    <div
      data-testid="state-offline"
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: '9px 16px',
        background: 'rgba(240,162,75,.1)',
        borderBottom: '1px solid rgba(240,162,75,.28)',
        fontSize: 12.5,
        color: color.amberText,
      }}
    >
      <span
        aria-hidden="true"
        style={{ width: 7, height: 7, borderRadius: '50%', background: color.amber }}
      />
      <span>
        لا يوجد اتصال — نعرض آخر بيانات محفوظة، والتحديث سيستأنف تلقائياً عند عودة الاتصال.
      </span>
    </div>
  );
}

// ---------------------------------------------------------------- Success

export function SuccessInline({ children }: { children: ReactNode }): JSX.Element {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, color: color.green, fontSize: 12.5 }}>
      <span
        aria-hidden="true"
        style={{ width: 6, height: 6, borderRadius: '50%', background: color.green }}
      />
      {children}
    </span>
  );
}
